import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { NavGroup } from '../models/nav.model';

const PERMISSIONS_KEY = 'dmp_permissions';
const PERMISSION_SCOPES_KEY = 'dmp_permission_scopes';

/** Permission key → ordered scope list (`PLATFORM` / `TENANT`). */
export type PermissionScopesMap = Record<string, string[]>;

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly permissionsSubject = new BehaviorSubject<Set<string>>(new Set());
  readonly permissions$ = this.permissionsSubject.asObservable();

  private readonly permissionScopesSubject = new BehaviorSubject<PermissionScopesMap>({});
  readonly permissionScopes$ = this.permissionScopesSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {
    this.loadFromStorage();
  }

  loadFromStorage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.permissionsSubject.next(new Set());
      this.permissionScopesSubject.next({});
      return;
    }

    const raw = sessionStorage.getItem(PERMISSIONS_KEY) ?? localStorage.getItem(PERMISSIONS_KEY);
    this.setPermissions(this.parsePermissions(raw));

    const scopesRaw =
      sessionStorage.getItem(PERMISSION_SCOPES_KEY) ?? localStorage.getItem(PERMISSION_SCOPES_KEY);
    this.setPermissionScopes(this.parsePermissionScopes(scopesRaw));
  }

  setPermissions(permissions: string[] | null | undefined): void {
    const normalized = (permissions ?? [])
      .map(permission => String(permission ?? '').trim())
      .filter(Boolean);
    this.permissionsSubject.next(new Set(normalized));
  }

  setPermissionScopes(scopes: PermissionScopesMap | null | undefined): void {
    this.permissionScopesSubject.next(this.normalizePermissionScopes(scopes));
  }

  clear(): void {
    this.permissionsSubject.next(new Set());
    this.permissionScopesSubject.next({});
  }

  getPermissionsSnapshot(): string[] {
    return [...this.permissionsSubject.value];
  }

  getPermissionScopesSnapshot(): PermissionScopesMap {
    return { ...this.permissionScopesSubject.value };
  }

  /** Scopes for a single permission key, or empty when unknown. */
  getScopesFor(permission: string): string[] {
    const key = String(permission ?? '').trim();
    if (!key) return [];
    return [...(this.permissionScopesSubject.value[key] ?? [])];
  }

  has(permission: string): boolean {
    return this.permissionsSubject.value.has(permission);
  }

  /** True when `dmp_permission_scopes[permission]` includes `scope`. */
  hasScope(permission: string, scope: string): boolean {
    const key = String(permission ?? '').trim();
    const requiredScope = String(scope ?? '').trim();
    if (!key || !requiredScope) return false;
    return this.getScopesFor(key).includes(requiredScope);
  }

  hasAny(required?: string | string[]): boolean {
    const normalized = this.normalizeRequired(required);
    if (normalized.length === 0) return true;
    const current = this.permissionsSubject.value;
    return normalized.some(permission => current.has(permission));
  }

  hasAll(required?: string | string[]): boolean {
    const normalized = this.normalizeRequired(required);
    if (normalized.length === 0) return true;
    const current = this.permissionsSubject.value;
    return normalized.every(permission => current.has(permission));
  }

  /**
   * Menu / feature gate: requires permission keys (any-of) and, when scopes are
   * provided, at least one of those permissions must also carry one of the scopes.
   *
   * Example (Tenants List):
   * `canAccess('TENANT_VIEW', 'PLATFORM')`
   * ≡ has TENANT_VIEW in dmp_permissions
   *   AND permissionScopes['TENANT_VIEW']?.includes('PLATFORM')
   */
  canAccess(requiredPermissions?: string | string[], requiredScopes?: string | string[]): boolean {
    const permissions = this.normalizeRequired(requiredPermissions);
    const scopes = this.normalizeRequired(requiredScopes);

    if (permissions.length === 0) {
      return scopes.length === 0;
    }

    if (!this.hasAny(permissions)) {
      return false;
    }

    if (scopes.length === 0) {
      return true;
    }

    return permissions.some(
      permission => this.has(permission) && scopes.some(scope => this.hasScope(permission, scope)),
    );
  }

  hasAny$(required?: string | string[]): Observable<boolean> {
    const normalized = this.normalizeRequired(required);
    return this.permissions$.pipe(
      map(current => normalized.length === 0 || normalized.some(permission => current.has(permission))),
    );
  }

  filterMenu(menu: NavGroup[]): NavGroup[] {
    return menu
      .map(group => ({
        ...group,
        items: group.items.filter(item => this.canAccess(item.permission, item.scopes)),
      }))
      .filter(group => this.canAccess(group.permission, group.scopes) && group.items.length > 0);
  }

  private normalizeRequired(required?: string | string[]): string[] {
    if (required == null) return [];
    return (Array.isArray(required) ? required : [required])
      .map(permission => String(permission ?? '').trim())
      .filter(Boolean);
  }

  private parsePermissions(raw: string | null): string[] {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(value => String(value ?? ''));
      if (Array.isArray(parsed?.dmp_permissions)) {
        return parsed.dmp_permissions.map((value: unknown) => String(value ?? ''));
      }
      return [];
    } catch {
      return [];
    }
  }

  private parsePermissionScopes(raw: string | null): PermissionScopesMap {
    if (!raw) return {};
    try {
      return this.normalizePermissionScopes(JSON.parse(raw) as PermissionScopesMap);
    } catch {
      return {};
    }
  }

  private normalizePermissionScopes(scopes: PermissionScopesMap | null | undefined): PermissionScopesMap {
    if (!scopes || typeof scopes !== 'object' || Array.isArray(scopes)) return {};

    const normalized: PermissionScopesMap = {};
    for (const [key, value] of Object.entries(scopes)) {
      const permission = String(key ?? '').trim();
      if (!permission) continue;
      const list = Array.isArray(value)
        ? value.map(scope => String(scope ?? '').trim()).filter(Boolean)
        : [];
      normalized[permission] = list;
    }
    return normalized;
  }
}
